import { KeyRound, RotateCcw, Trash2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import { Avatar } from '../../../../components/Avatar';
import { AvatarCell } from '../../../../components/table/AvatarCell';
import { DateCell } from '../../../../components/table/DateCell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import type { MenuItem } from '../../../../components/Menu';
import { BulkDeactivateDialog } from '../components/BulkDeactivateDialog';
import { CreateUserModal } from '../components/CreateUserModal';
import { UserConfirmDialog, type UserConfirmKind } from '../components/UserConfirmDialog';
import { usersApi } from '../usersApi';
import type { UserListItem } from '../userTypes';

/**
 * Users list. Configures columns, facets, and the endpoint — the layout comes entirely
 * from `<IndexPage>` (reference §3). This page owns no chrome of its own.
 */
export function UsersIndexPage() {
  const navigate = useNavigate();

  const [createOpen, setCreateOpen] = useState(false);
  const [confirming, setConfirming] = useState<{ kind: UserConfirmKind; user: UserListItem } | null>(
    null,
  );
  const [bulkDeactivate, setBulkDeactivate] = useState<UserListItem[] | null>(null);

  /**
   * Bumped after every mutation. The shell refetches, which refreshes the rows, the record
   * count, and the facet counts together — the cross-component fan-out the reference
   * requires as part of definition-of-done.
   */
  const [reloadKey, setReloadKey] = useState(0);
  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchUsers = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      usersApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (user: UserListItem): MenuItem[] => [
      {
        key: `reset-${user.id}`,
        label: 'Reset password',
        icon: <KeyRound className="h-4 w-4" aria-hidden="true" />,
        permission: 'users.reset-password',
        onClick: () => setConfirming({ kind: 'reset-password', user }),
      },
      // The mandatory reversal of the soft delete, offered on the same surface.
      {
        key: `activate-${user.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'users.activate',
        disabledReason: user.isActive ? 'This account is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', user }),
      },
    ],
    [],
  );

  const bulkActions = useCallback(
    (selected: UserListItem[]): MenuItem[] => {
      const deactivatable = selected.filter((u) => u.isActive);

      return [
        {
          key: 'bulk-deactivate',
          label: `Deactivate ${deactivatable.length} user(s)`,
          icon: <Trash2 className="h-4 w-4" aria-hidden="true" />,
          permission: 'users.delete',
          destructive: true,
          disabledReason:
            deactivatable.length === 0 ? 'Every selected account is already inactive.' : null,
          onClick: () => setBulkDeactivate(deactivatable),
        },
      ];
    },
    [],
  );

  return (
    <>
      <IndexPage<UserListItem>
        module="users"
        breadcrumb={['Administration', 'Users']}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['ApplicationUser']}
        rowKey={(user) => user.id}
        fetch={fetchUsers}
        reloadKey={reloadKey}
        searchPlaceholder="Search alias or email"
        emptyMessage="No users yet."
        onCreate={() => setCreateOpen(true)}
        createPermission="users.create"
        rowActions={rowActions}
        // Editing lives on the profile, one card at a time, so the row menu offers no Edit
        // of its own. The anchor is how you get there.
        onRowOpen={(user) => navigate(`/users/${user.id}`)}
        openPermission="users.read"
        onDelete={(user) => setConfirming({ kind: 'deactivate', user })}
        deletePermission="users.delete"
        deleteLabel={() => 'Deactivate'}
        // Explicit only because Users' bulk affordance is its own dialog, not `onDelete`.
        selectable
        bulkActions={bulkActions}
        onClearData={() => usersApi.clear()}
        clearDataPermission="users.clear-data"
        otherInfo={(user) => user}
      />

      <CreateUserModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          refresh();
        }}
      />

      <BulkDeactivateDialog
        users={bulkDeactivate}
        onClose={() => setBulkDeactivate(null)}
        onDone={() => {
          setBulkDeactivate(null);
          refresh();
        }}
      />

      <UserConfirmDialog
        request={confirming}
        onClose={() => setConfirming(null)}
        onDone={() => {
          setConfirming(null);
          refresh();
        }}
      />
    </>
  );
}

/**
 * Column definitions. `Alias` is the frozen anchor and carries the soft flex, so it absorbs
 * leftover width until the user drags it.
 */
const COLUMNS: ColumnDef<UserListItem>[] = [
  /*
    `summary` nominates the card view's sub-information line. An account is identified by its
    alias, its email, and its roles — there is nothing else about a person on record here.
  */
  {
    key: 'alias',
    header: 'Alias',
    width: 260,
    flex: true,
    sortable: true,
    // The card's avatar spans both of its lines, so it is hoisted out of `render` there.
    avatar: (user) => <Avatar name={user.displayName} email={user.email} size="md" />,
    render: (user) => (
      <AvatarCell
        // displayName falls back to the email, so initials are never derived from a blank.
        name={user.displayName}
        email={user.email}
        primary={user.displayName}
        size="sm"
      />
    ),
  },
  {
    key: 'email',
    header: 'Email',
    width: 260,
    sortable: true,
    summary: true,
    render: (user) => <TextCell value={user.email} mono />,
  },
  {
    key: 'roleName',
    header: 'Role',
    width: 170,
    summary: true,
    render: (user) => <TextCell value={user.roleName} />,
  },
  {
    key: 'lastLoginAt',
    header: 'Last sign-in',
    width: 175,
    sortable: true,
    // Blank when the account has never signed in — an empty cell, not the word "Never".
    render: (user) => <DateCell value={user.lastLoginAt} fallback="" />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (user) =>
      user.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Inactive</StatusText>
      ),
  },
];
